import { Link, useParams, Navigate } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo from "@/components/Seo";
import { ArrowRight, ArrowLeft, MapPin, House, PersonSimpleWalk, Coffee, GraduationCap, Check } from "@phosphor-icons/react";
import { findSuburb, REGIONS, slug } from "@/lib/areas";

export default function SuburbPage() {
  const { suburb } = useParams();
  const match = findSuburb(suburb);
  if (!match) return <Navigate to="/service-areas" replace />;
  const { suburb: name, region, hall } = match;

  const nearby = REGIONS.find((r) => r.suburbs.includes(name))?.suburbs.filter((s) => s !== name).slice(0, 6) || [];
  const mapsQuery = encodeURIComponent(`${name} QLD Australia`);

  const services = [
    { icon: GraduationCap, title: "Puppy School (group classes)", desc: `Small friendly group classes at our nearest hall — ${hall.name}.`, color: "#f38838", slug: "puppy-school" },
    { icon: House, title: "Private Puppy Consults", desc: `the team travels to your home in ${name} for 1:1 sessions, tailored to your puppy.`, color: "#1b4a76", slug: "private-consults" },
    { icon: PersonSimpleWalk, title: "Puppy Walk & Trains", desc: `We pick up your puppy from ${name} for a play, socialisation and training session.`, color: "#E2B054", slug: "walk-and-trains" },
    { icon: Coffee, title: "Puppy Home Check-Ins", desc: `30-minute drop-ins for puppies in ${name} while you're at work.`, color: "#f38838", slug: "home-check-ins" },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title={`Puppy Training in ${name} | Puppl`}
        description={`Sunshine Coast puppy training in ${name} with the Puppl team — private consults, puppy school at ${hall.name}, walk & trains and home check-ins. Fear-free, science-based methods, 1000+ puppies trained.`}
        canonical={typeof window !== "undefined" ? `${window.location.origin}/service-areas/${slug(name)}` : ""}
      />
      <PublicHeader />

      <section className="bg-[#1b4a76] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <Link to="/service-areas" className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white mb-4" data-testid="back-to-areas">
            <ArrowLeft size={14} /> All service areas
          </Link>
          <p className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-2">{region}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4" data-testid="suburb-title">
            Puppy training in {name}.
          </h1>
          <p className="text-lg lg:text-xl opacity-90 leading-relaxed max-w-2xl">
            the Puppl team services {name} with in-home private puppy consults, walk &amp; trains, and home check-ins — plus small-group puppy school at our nearest hall.
          </p>
        </div>
      </section>

      {/* INFO ROW */}
      <section className="py-12 px-6 lg:px-12 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-pp overflow-hidden">
            <div className="aspect-[16/10] bg-stone-100">
              <iframe
                title={`Map of ${name}`}
                src={`https://maps.google.com/maps?q=${mapsQuery}&z=13&output=embed`}
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="p-5">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#f38838] mb-1">Your area</div>
              <div className="font-heading font-semibold text-lg text-stone-900">{name}, Sunshine Coast QLD</div>
            </div>
          </div>

          <div className="card-pp p-6 flex flex-col">
            <div className="text-xs uppercase tracking-wider font-semibold text-[#1b4a76] mb-2">Closest Puppy School hall</div>
            <h2 className="font-heading font-semibold text-2xl text-stone-900 mb-2">{hall.name}</h2>
            <p className="text-stone-600 mb-2">{hall.address}</p>
            <div className="text-sm font-semibold text-stone-700 mb-4">{hall.times}</div>
            <ul className="text-sm text-stone-700 space-y-1.5 mb-4">
              {["$149 AUD for 4 weeks of classes", "Max 6 puppies per class", "Online video library + treat pouch included", "Email help between classes"].map((b, i) => (
                <li key={i} className="flex items-start gap-2"><Check size={14} weight="bold" className="text-[#1b4a76] mt-1 flex-shrink-0" /> {b}</li>
              ))}
            </ul>
            <div className="mt-auto flex gap-3 flex-wrap">
              <Link to="/register" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="suburb-cta-book">
                Book Now <ArrowRight size={14} weight="bold" />
              </Link>
              <a
                href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(name + " QLD")}&destination=${encodeURIComponent(hall.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-stone-700 hover:text-[#f38838] inline-flex items-center px-3 py-2.5"
              >
                Directions from {name}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES AVAILABLE */}
      <section className="py-12 px-6 lg:px-12 max-w-6xl mx-auto">
        <h2 className="font-heading text-2xl lg:text-3xl font-bold text-stone-900 mb-2">Services in {name}</h2>
        <p className="text-stone-600 mb-8">All Puppl services are available to {name} families.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {services.map((s, i) => (
            <Link key={i} to={`/services/${s.slug}`} className="card-pp p-6 hover:-translate-y-0.5 hover:shadow-md transition-all block group" data-testid={`suburb-service-${s.slug}`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ background: `${s.color}15` }}>
                <s.icon size={26} weight="duotone" style={{ color: s.color }} />
              </div>
              <h3 className="font-heading font-semibold text-lg text-stone-900 mb-1 group-hover:text-[#f38838] transition-colors">{s.title}</h3>
              <p className="text-sm text-stone-600">{s.desc}</p>
              <span className="text-sm font-semibold text-[#f38838] mt-3 inline-flex items-center gap-1">Learn more <ArrowRight size={14} weight="bold" /></span>
            </Link>
          ))}
        </div>
        <Link to="/services" className="inline-flex items-center gap-2 mt-6 font-semibold text-[#f38838] hover:text-[#d97324]">
          See full service list <ArrowRight size={16} weight="bold" />
        </Link>
      </section>

      {/* NEARBY */}
      {nearby.length > 0 && (
        <section className="py-12 px-6 lg:px-12 max-w-6xl mx-auto">
          <h3 className="font-heading text-xl font-semibold text-stone-900 mb-4">Nearby suburbs we also service</h3>
          <div className="flex flex-wrap gap-2">
            {nearby.map((s) => (
              <Link
                key={s}
                to={`/service-areas/${slug(s)}`}
                className="text-sm bg-white border border-stone-200 hover:border-[#f38838] hover:text-[#f38838] px-4 py-2 rounded-full font-medium text-stone-700 transition-colors inline-flex items-center gap-1.5"
                data-testid={`nearby-${slug(s)}`}
              >
                <MapPin size={12} weight="fill" /> {s}
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="py-16 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto bg-[#f38838] rounded-3xl p-10 lg:p-14 text-white text-center relative overflow-hidden">
          <div className="absolute inset-0 pp-noise opacity-20" />
          <div className="relative">
            <h2 className="font-heading text-2xl lg:text-4xl font-bold mb-3">Ready for puppy training in {name}?</h2>
            <p className="opacity-90 mb-6">Create a free account or chat with our assistant for instant answers.</p>
            <Link to="/register" className="bg-white text-[#f38838] hover:bg-[#FDFBF7] rounded-xl px-8 py-3 font-semibold inline-flex items-center gap-2 transition-colors">
              Create free account <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />

      <ChatWidget />
    </div>
  );
}
